import React, { Fragment, useState, useEffect, useRef } from "react";
import { Row, Col, Modal, Form, Select, Switch, Drawer, Spin, Alert, Tag } from "antd";
import { UserOutlined, WarningOutlined } from "@ant-design/icons";
import { Scrollbars } from "react-custom-scrollbars";
import closeX from "../../../../assets/images/close-x.svg";
import db from "../../../../database";
import { getMultilingualText } from "../../../../utils/getMultilingualText";
import { validateGSTNumber } from "../../../../services/index";
import useAuthStore from "../../../../stores/useAuthStore";
import useCartStore from "../../../../stores/useCartStore";
import useTransactionStore from "../../../../stores/useTransactionStore";

// ── Extracted sub-components ─────────────────────────────────────────────────
import useCustomerFormData from "./useCustomerFormData";
import CustomerFormField from "./CustomerFormField";
import B2BSearchBar from "./B2BSearchBar";
import CustomerOperationalToggles from "./CustomerOperationalToggles";
import LegacyContactForm from "./LegacyContactForm";
import CustomerDrawerFooter from "./CustomerDrawerFooter";
import CustomerCouponTab from "./CustomerCouponTab";
import { toCamelCase, buildGroupedFields } from "./customerFormUtils";

const { Option } = Select;

const AddCustomerDrawer = (props) => {
  const {
    displayAddNewCustomer,
    setDisplayAddNewCustomer,
    setDisplayCustomerSearch,
    customerForm,
    form,
    addNewCustomer,
    editOldCustomer,
    customerSearchInput,
    setCustomerSearchInput,
    setCustomerSearchResults,
    retailCustomerTabs,
    retailCustomerFields,
    retailCustomerGroups,
    retailCustomerRules,
    retailCustomerb2bSettings,
    retailCustomerApiDataSources,
    activeTab,
    setActiveTab,
    activeTabName,
    setActiveTabName,
    isB2BEnabled,
    setIsB2BEnabled,
    formDataCache,
    setFormDataCache,
    expandedGroups,
    setExpandedGroups,
    switchToggle,
    setSwitchToggle,
    isViewMode,
    setIsViewMode,
    toCamelCaseMetaData,
    convertMetaDataToKeyValue,
    isSubmittingCustomer,
    setIsSubmittingCustomer,
    lastSearchedValue,
    setLastSearchedValue,
    selectedB2BCustomer,
    setSelectedB2BCustomer,
    posConfig,
    cart,
    refs,
    setKeyboardInputFields,
    keyboardInputFields,
    keyboard,
    setOpen,
    open,
    setInputName,
    inputName,
    setOpenSalesRep,
    selectedEditOldCustomer,
    setSelectedEditOldCustomer,
    customerConfigData,
    setKioskUI,
    kioskLogin,
    loading,
    setLoading,
    isPanCustomer,
    setIsPanCustomer,
    panCustomerInfo,
    setPanCustomerInfo,
    documents,
    setDocuments,
    isNriBilling,
    setIsNriBilling,
    isCreditSale,
    setIsCreditSale,
    retailCustomerOperationalSettings,
    displayEditOldCustomer,
    pendingRefundCustomer,
    setPendingRefundCustomer,
    isRefundCustomerEdit,
    setIsRefundCustomerEdit,
    searchInputRef,
    setCustomerLoading,
    customerLoading,
    panDrawerMode,
    gstInfo,
    setGstInfo,
    selectedInvoiceAddress,
    setSelectedInvoiceAddress,
    selectedDeliveryAddress,
    setSelectedDeliveryAddress,
    processCouponApplication,
    setAdditionalInfo,
    setShowCouponOtpPopup,
    setCouponVerificationData,
    setDisplayEditOldCustomer,
  } = props;

  const tillData = useAuthStore((s) => s.tillData);
  const currenciesList = tillData.tillAccess.csBunit.currencies;

  // ── Internal state ───────────────────────────────────────────────────────
  const [toggleCustomer, setToggleCustomer] = useState(false);
  const [loyalityType, setLoyalityType] = useState([]);
  const [enableLoyality, setEnableLoyality] = useState(true);
  const [invoiceAddressList, setInvoiceAddressList] = useState([]);
  const [deliveryAddressList, setDeliveryAddressList] = useState([]);
  const [showB2BDropdown, setShowB2BDropdown] = useState(false);
  const [isPanUpdateMode, setIsPanUpdateMode] = useState(false);

  const searchContainerRef = useRef(null);

  const showVirtualKeyboard = posConfig?.showKeyboard === "Y";

  const iconMap = {
    user: <UserOutlined />,
    info: null,
    building: null,
    location: null,
    payment: null,
    folder: null,
  };

  const exchangeCart = useCartStore((s) => s.exchangeCart);
  const activeCart = useTransactionStore((s) => s.selectedReturnType) === "EXCHANGE" && exchangeCart?.items?.length > 0 ? exchangeCart : cart;
  const panRule = customerConfigData?.conditionalRules?.find((rule) => rule.action === "Request PAN");
  const isPanMandatoryNow = panRule && Number(activeCart?.total) > Number(panRule.value);
  const enteredPAN = activeCart?.customer?.panCard || activeCart?.customer?.metaData?.find((m) => m.key?.toLowerCase().startsWith("pan"))?.value;

  // ── groupedFields (memoised via IIFE) ───────────────────────────────────
  const groupedFields = buildGroupedFields(retailCustomerGroups, retailCustomerFields, activeTab, activeTabName, isB2BEnabled);

  // ── Custom hook: all data/logic ──────────────────────────────────────────
  const {
    optionsMap,
    b2bSearchResults,
    setB2bSearchResults,
    b2bLoading,
    searchPending,
    searchByName,
    setSearchByName,
    handleChange,
    searchB2BCustomers,
    mapB2BDataDynamic,
    fillAddressFields,
    mapGSTToFormValuesDynamic,
    handleApiEnterValidation,
    validatePanDirect,
    fetchOptions,
    handleFileUpload,
  } = useCustomerFormData({
    customerForm,
    form,
    activeTab,
    activeTabName,
    isB2BEnabled,
    setIsB2BEnabled,
    formDataCache,
    setFormDataCache,
    retailCustomerFields,
    retailCustomerApiDataSources,
    groupedFields,
    cart,
    tillData,
    posConfig,
    customerConfigData,
    setLoading,
    setSelectedEditOldCustomer,
    setIsPanUpdateMode,
    setPanCustomerInfo,
    setKeyboardInputFields,
    keyboardInputFields,
    setKioskUI,
    kioskLogin,
    pendingRefundCustomer,
    setPendingRefundCustomer,
    isRefundCustomerEdit,
    setIsRefundCustomerEdit,
    setCustomerSearchResults,
    displayAddNewCustomer,
    displayEditOldCustomer,
    setDocuments,
  });

  // ── Auto-trigger PAN validation when toggle is enabled + PAN value present
  useEffect(() => {
    if (!isPanCustomer) return;
    const allValues = customerForm.getFieldsValue(true);
    const panKey = Object.keys(allValues).find((k) => k.toLowerCase().startsWith("pan") && !k.toLowerCase().includes("file"));
    const panValue = panKey ? allValues[panKey]?.trim?.() : null;
    if (panValue && panValue.length === 10 && !panCustomerInfo) {
      validatePanDirect(panValue);
    }
  }, [isPanCustomer]);

  useEffect(() => {
    if (displayAddNewCustomer || displayEditOldCustomer) {
      const storedB2B = localStorage.getItem("isB2bEnabled") === "true";
      setIsB2BEnabled(storedB2B);
      const storedPanCustomer = localStorage.getItem("isPanCustomer") === "true" && panDrawerMode !== "VALIDATION";
      setIsPanCustomer(storedPanCustomer);
      const storedNriBilling = localStorage.getItem("isNriBilling") === "true";
      setIsNriBilling(storedNriBilling);
      const storedCreditSale = useTransactionStore.getState().isCreditSale === "true" || localStorage.getItem("isCreditSale") === "true";
      setIsCreditSale(storedCreditSale);
    }
  }, [displayAddNewCustomer, displayEditOldCustomer]);

  // ── Load loyalty on mount ────────────────────────────────────────────────
  useEffect(() => {
    const run = async () => {
      const loyaliryData = await db.loyalityData.toArray();
      loyaliryData.forEach((item) => {
        if (item.isDefault === "Y") setLoyalityType([item]);
      });
    };
    run();
  }, []);

  // ── Sync form values display ─────────────────────────────────────────────
  useEffect(() => {
    customerForm.getFieldsValue();
  }, [displayAddNewCustomer, displayEditOldCustomer]);

  // ── Expanded-groups initialisation ──────────────────────────────────────
  useEffect(() => {
    const initState = {};
    groupedFields.forEach((group) => {
      if (group.collapsible) initState[group.groupId] = false;
    });
    setExpandedGroups(initState);
  }, [activeTab]);

  // ── Click-outside handler for B2B dropdown ───────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowB2BDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Scrollbar helpers ────────────────────────────────────────────────────
  const renderThumb = ({ style, ...props }) => <div style={{ ...style, backgroundColor: "#c1c1c1", borderRadius: "5px", width: "8px" }} {...props} />;
  const renderThumbHorizontalScroll = ({ style, ...props }) => <div style={{ ...style, width: "0px" }} {...props} />;

  const copyInvoiceToDelivery = () => {
    const allValues = customerForm.getFieldsValue(true);
    const businessGroups = groupedFields.filter((g) => g.groupName?.toLowerCase().includes("invoic") || g.groupName?.toLowerCase().includes("deliver"));
    const invoiceGroup = businessGroups.find((g) => g.groupName?.toLowerCase().includes("invoic"));
    const deliveryGroup = businessGroups.find((g) => g.groupName?.toLowerCase().includes("deliver"));
    if (!invoiceGroup || !deliveryGroup) return;
    const invoiceData = allValues[invoiceGroup.groupId] || {};
    const copiedValues = { [deliveryGroup.groupId]: { ...invoiceData } };
    customerForm.setFieldsValue(copiedValues);
    setFormDataCache((prev) => ({
      ...prev,
      [activeTab]: { ...(prev[activeTab] || {}), ...copiedValues },
    }));
  };

  // ── Misc helpers ─────────────────────────────────────────────────────────
  const onSwitchChange = (checked) => {
    form.setFieldsValue({ loyality: checked });
    setKeyboardInputFields({ ...keyboardInputFields, loyalty: checked });
    setEnableLoyality(checked);
  };

  const addCustomer = () => {
    return customerForm
      .validateFields()
      .then((values) => {
        if (values.name !== undefined) return addNewCustomer(values);
      })
      .catch((errorInfo) => {
        console.debug("Validation Failed:", errorInfo);
      });
  };

  const onFocusInput = (fieldName) => {
    setInputName(fieldName);
    setOpen(true);
    setTimeout(() => {
      const currentInputValue = keyboardInputFields[fieldName] || "";
      keyboard?.current?.setInput(currentInputValue);
    }, 300);
  };

  const onChangeInput = (event) => {
    const inputVal = event.target.value;
    setKeyboardInputFields({ ...keyboardInputFields, [inputName]: inputVal });
    keyboard?.current?.setInput(inputVal);
  };

  const getInputValue = (fieldName) => keyboardInputFields[fieldName] || "";

  const clearPanFields = () => {
    const allValues = customerForm.getFieldsValue(true);
    const clearedValues = {};
    Object.entries(allValues).forEach(([key, value]) => {
      if (typeof value === "object" && value !== null) {
        clearedValues[key] = { ...value };
        Object.keys(value).forEach((innerKey) => {
          if (innerKey.toLowerCase().includes("pan")) clearedValues[key][innerKey] = "";
        });
      } else if (key.toLowerCase().includes("pan")) {
        clearedValues[key] = "";
      }
    });
    customerForm.setFieldsValue(clearedValues);
  };

  const renderConditionalRule = () => {
    const rule = customerConfigData?.conditionalRules?.find((r) => r.action === "Request PAN");
    if (rule && activeCart?.total > parseFloat(rule.value)) {
      return (
        <Alert
          message={
            <span>
              <p style={{ color: "#B45309", fontSize: "14px", fontWeight: 300, marginBottom: 0, marginLeft: "0.8vw" }}>
                {String.fromCharCode(8226)} {rule.description}
              </p>
            </span>
          }
          type="warning"
          showIcon
          icon={<WarningOutlined style={{ color: "#0F0718" }} />}
          style={{
            backgroundColor: "#fff8f0",
            borderColor: "#ffecb5",
            color: "#b26a00",
            fontWeight: 500,
            width: "100%",
            borderRadius: "5px",
            height: "7vh",
            marginTop: "1.5vh",
            marginBottom: "1.5vh",
          }}
        />
      );
    }
    return null;
  };

  const renderScrollHeight = () => activeCart?.total > parseFloat(customerConfigData?.conditionalRules?.[0]?.value);

  const hasCustomFields = retailCustomerFields && retailCustomerFields.length > 0;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Fragment>
      <Drawer open={displayAddNewCustomer} placement="right" footer={null} closable={false} styles={{ body: { paddingTop: 0, backgroundColor: "#ffffff" } }} width="62%">
        <Spin spinning={loading}>
          <div
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <Form key={"addCustomer"} form={customerForm} name="addCustomer">
              {/* ── Header ──────────────────────────────────────────── */}
              <Row style={{ display: "flex", alignItems: "center", marginTop: "2.5vh", justifyContent: "center" }}>
                <Col span={16} style={{ display: "flex", alignItems: "center" }}>
                  <UserOutlined style={{ fontSize: "20px", marginRight: "0.3em" }} />
                  <p style={{ marginBottom: 0, fontSize: "1.5em", fontWeight: 500, color: "#0F0718" }}>Customer Registration</p>
                </Col>
                {cart?.total > 0 ? (
                  <Col style={{ display: "flex", alignItems: "center", justifyContent: "right", marginTop: "0.7vh" }} span={6}>
                    <p style={{ border: "1px solid #D9D9D9", borderRadius: "15px", marginBottom: 0, color: "#0F0718", padding: "1.5px 10px", fontSize: "1em", fontWeight: 600 }}>
                      Bill Value: {currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""}
                      {cart?.total}
                      {currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}
                    </p>
                  </Col>
                ) : (
                  <Col span={6} />
                )}
                <Col span={2} style={{ textAlign: "right", marginTop: "0.7vh" }}>
                  <img
                    src={closeX}
                    style={{ cursor: "pointer", width: "1.5vw" }}
                    onClick={() => {
                      if (isPanMandatoryNow && (!enteredPAN || enteredPAN.trim() === "")) {
                        Modal.warning({
                          title: "PAN Required",
                          content: "PAN is mandatory for this transaction. Please enter PAN to continue.",
                          okText: "OK",
                          cancelButtonProps: { style: { display: "none" } },
                        });
                        return;
                      }
                      const isDefaultCustomer = cart?.customer?.cwrCustomerId === tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId;
                      const isCustomerMandatory = isDefaultCustomer && posConfig?.customerRequired === "Y";
                      if (isCustomerMandatory) {
                        setDisplayCustomerSearch(true); // ← open customer search
                      }
                      if (cart?.customer?.b2cRegisteredstoreId === null) {
                        setDisplayCustomerSearch(true);
                      }
                      if (cart?.customer?.b2cRegisteredstoreId && posConfig?.requireSalesRepresentative === "Y") {
                        const exchangeCartData = useCartStore.getState().exchangeCart;
                        if (exchangeCartData) {
                          if (exchangeCartData?.salesRepId === null) setOpenSalesRep({ flag: true, level: "order" });
                        } else {
                          if (cart?.salesRepId === null) setOpenSalesRep({ flag: true, level: "order" });
                        }
                      }
                      setDisplayAddNewCustomer(false);
                      setCustomerSearchInput("");
                      setIsB2BEnabled(false);
                      localStorage.removeItem("isB2bEnabled");
                      useTransactionStore.getState().setIsB2bEnabled(null);
                      setIsPanUpdateMode(false);
                      setFormDataCache({});
                      setActiveTab(retailCustomerTabs.filter((tab) => tab.visible).sort((a, b) => a.order - b.order)[0]?.id || "");
                      setActiveTabName(retailCustomerTabs.filter((tab) => tab.visible).sort((a, b) => a.order - b.order)[0]?.name || "");
                      setCustomerSearchResults();
                      customerForm.resetFields();
                      setLastSearchedValue("");
                      setSelectedB2BCustomer(null);
                      setB2bSearchResults([]);
                      setIsPanCustomer(false);
                      setIsNriBilling(false);
                      setIsCreditSale(false);
                      setPanCustomerInfo(null);
                      setDocuments([]);
                      if (setGstInfo) setGstInfo(null);
                    }}
                    alt=""
                  />
                </Col>
              </Row>

              <Row style={{ marginTop: "1vh" }}>
                <p style={{ marginBottom: 0, color: "#6B7280", fontSize: "14px", fontWeight: 100 }}>Register a new customer with complete details</p>
              </Row>

              {/* ── PAN / validation alerts ─────────────────────── */}
              <Row>
                {panDrawerMode === "THRESHOLD" && renderConditionalRule()}
                {panDrawerMode === "VALIDATION" && (
                  <Alert
                    message={
                      <span>
                        <p style={{ color: "#B45309", fontSize: "14px", fontWeight: 300, marginBottom: 0, marginLeft: "0.8vw" }}>
                          • Please enable PAN Customer and validate the PAN to proceed.
                        </p>
                      </span>
                    }
                    type="warning"
                    showIcon
                    icon={<WarningOutlined style={{ color: "#0F0718" }} />}
                    style={{
                      backgroundColor: "#fff8f0",
                      borderColor: "#ffecb5",
                      color: "#b26a00",
                      fontWeight: 500,
                      width: "100%",
                      borderRadius: "5px",
                      height: "7vh",
                      marginTop: "1.5vh",
                      marginBottom: "1.5vh",
                    }}
                  />
                )}
              </Row>

              {/* ══════════════════════════════════════════════════════
                  DYNAMIC FORM (retailCustomerFields configured)
              ══════════════════════════════════════════════════════ */}
              {hasCustomFields ? (
                <>
                  {/* ── Tab bar ─────────────────────────────────── */}
                  <Row
                    style={{
                      backgroundColor: "#f5f5f5",
                      padding: "6px",
                      borderRadius: "8px",
                      marginTop: "2vh",
                      height: "8vh",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {retailCustomerTabs
                      .filter((tab) => tab.visible)
                      .sort((a, b) => a.order - b.order)
                      .map((tab) => {
                        const isActive = tab.id === activeTab;
                        const isDisabled = tab.name.toLowerCase() === "business" && (!customerConfigData?.b2bSettings?.enableB2BBilling || isNriBilling);

                        return (
                          <Col
                            key={tab.id}
                            onClick={async () => {
                              if (isDisabled) return;
                              const currentValues = await customerForm.getFieldsValue();
                              const updatedCache = {
                                ...formDataCache,
                                [activeTab]: currentValues,
                              };
                              setFormDataCache(updatedCache);

                              if (tab.name?.toLowerCase().includes("business")) {
                                const allCached = Object.values(updatedCache).reduce((acc, v) => ({ ...acc, ...v }), {});
                                const hasGst = Object.entries(allCached).some(([k, v]) => {
                                  if (k.toLowerCase().includes("gst") && v?.toString().trim()) return true;
                                  if (/^group_\d+$/.test(k) && typeof v === "object" && v) {
                                    return Object.entries(v).some(([fk, fv]) => fk.toLowerCase().includes("gst") && fv?.toString().trim());
                                  }
                                  return false;
                                });
                                const hasPan = Object.entries(allCached).some(([k, v]) => {
                                  if (k.toLowerCase().startsWith("pan") && v?.toString().trim()) {
                                    return true;
                                  }
                                  if (/^group_\d+$/.test(k) && typeof v === "object" && v) {
                                    return Object.entries(v).some(([fk, fv]) => fk.toLowerCase().startsWith("pan") && fv?.toString().trim());
                                  }
                                  return false;
                                });
                                if (!hasGst && hasPan && isPanCustomer) {
                                  const contactTabIds = retailCustomerTabs.filter((t) => t.name?.toLowerCase().includes("contact")).map((t) => t.id);
                                  const contactGroups = (retailCustomerGroups || []).filter((g) => contactTabIds.includes(g.tabId) && g.name?.toLowerCase().includes("address"));
                                  const businessTabIds = retailCustomerTabs.filter((t) => t.name?.toLowerCase().includes("business")).map((t) => t.id);
                                  const businessGroups = (retailCustomerGroups || []).filter((g) => businessTabIds.includes(g.tabId) && g.name?.toLowerCase().includes("address"));
                                  let contactAddressData = null;
                                  for (const cg of contactGroups) {
                                    const groupData = updatedCache[contactTabIds[0]]?.[cg.id] || allCached[cg.id];
                                    if (groupData && typeof groupData === "object" && Object.values(groupData).some((v) => v?.toString().trim())) {
                                      contactAddressData = groupData;
                                      break;
                                    }
                                  }
                                  if (contactAddressData && businessGroups.length > 0) {
                                    const businessTabCache = updatedCache[tab.id] || {};
                                    for (const bg of businessGroups) {
                                      const existingBiz = businessTabCache[bg.id];
                                      const hasExistingData = existingBiz && typeof existingBiz === "object" && Object.values(existingBiz).some((v) => v?.toString().trim());
                                      if (!hasExistingData) {
                                        businessTabCache[bg.id] = { ...contactAddressData };
                                      }
                                    }
                                    updatedCache[tab.id] = businessTabCache;
                                    setFormDataCache({ ...updatedCache });
                                  }
                                }
                              }

                              if (updatedCache[tab.id]) {
                                customerForm.setFieldsValue(updatedCache[tab.id]);
                              }
                              setActiveTab(tab.id);
                              setActiveTabName(tab.name);
                            }}
                            style={{
                              flex: 1,
                              padding: "6px 0",
                              borderRadius: "6px",
                              backgroundColor: isActive ? "#fff" : "transparent",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: isActive ? "#000" : "#8c8c8c",
                              fontWeight: isActive ? 600 : 500,
                              boxShadow: isActive ? "0px 0px 4px rgba(0,0,0,0.05)" : "none",
                              transition: "all 0.2s ease",
                            }}
                          >
                            <span style={{ marginRight: 6 }}>{iconMap[tab.icon]}</span>
                            {tab.name}
                          </Col>
                        );
                      })}
                  </Row>

                  {/* ── Scrollable form body ─────────────────────── */}
                  <Scrollbars style={{ height: renderScrollHeight() ? "55vh" : "65vh" }} autoHide>
                    <div style={{ width: "99%" }}>
                      {/* ── B2B search bar ─────────────────────── */}
                      {activeTabName === "Business" && customerConfigData?.b2bSettings?.enableB2BBilling && (
                        <div
                          style={{
                            paddingLeft: "1px",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginTop: "25px",
                            marginBottom: "16px",
                          }}
                          ref={searchContainerRef}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "1.5vw" }}>
                            <div style={{ fontWeight: 600, fontSize: "16px" }}>Business Customer (B2B)</div>
                            <Switch
                              checked={isB2BEnabled}
                              // disabled={panCustomerInfo?.isValid === true}
                              onChange={async (checked) => {
                                if (panCustomerInfo?.isValid === true) return;
                                if (checked) {
                                  const gstField = customerForm.getFieldValue("gstNo") || customerForm.getFieldValue("gstNumber") || "";
                                  if (gstField.trim()) {
                                    try {
                                      setLoading(true);
                                      const gstResponse = await validateGSTNumber(gstField.trim());
                                      if (gstResponse?.valid) {
                                        setGstInfo(gstResponse);
                                        setIsB2BEnabled(true);
                                        setSwitchToggle(true);
                                        localStorage.setItem("isB2bEnabled", true);
                                        useTransactionStore.getState().setIsB2bEnabled("true");
                                      } else {
                                        Modal.error({ title: "GST Validation Failed", content: "The GST number could not be validated. Please check and try again." });
                                      }
                                    } catch {
                                      Modal.error({ title: "GST Validation Error", content: "Unable to validate GST number. Please try again." });
                                    } finally {
                                      setLoading(false);
                                    }
                                    return;
                                  }
                                } else {
                                  setGstInfo(null);
                                }
                                setIsB2BEnabled(checked);
                                localStorage.setItem("isB2bEnabled", checked);
                                useTransactionStore.getState().setIsB2bEnabled(String(checked));
                              }}
                            />
                            {gstInfo?.gstStatus && (
                              <Tag
                                color={gstInfo?.gstStatus?.toUpperCase() === "ACTIVE" ? "green" : gstInfo?.gstStatus?.toUpperCase() === "CANCELLED" ? "red" : "orange"}
                                style={{ fontSize: "12px" }}
                              >
                                {gstInfo?.gstStatus}
                              </Tag>
                            )}
                          </div>

                          {isB2BEnabled && (
                            <B2BSearchBar
                              lastSearchedValue={lastSearchedValue}
                              setLastSearchedValue={setLastSearchedValue}
                              isViewMode={isViewMode}
                              customerConfigData={customerConfigData}
                              searchByName={searchByName}
                              setSearchByName={setSearchByName}
                              b2bLoading={b2bLoading}
                              searchPending={searchPending}
                              b2bSearchResults={b2bSearchResults}
                              setB2bSearchResults={setB2bSearchResults}
                              selectedB2BCustomer={selectedB2BCustomer}
                              setSelectedB2BCustomer={setSelectedB2BCustomer}
                              showB2BDropdown={showB2BDropdown}
                              setShowB2BDropdown={setShowB2BDropdown}
                              searchB2BCustomers={searchB2BCustomers}
                              mapB2BDataDynamic={mapB2BDataDynamic}
                              groupedFields={groupedFields}
                              activeTab={activeTab}
                              customerForm={customerForm}
                              setFormDataCache={setFormDataCache}
                              setInvoiceAddressList={setInvoiceAddressList}
                              setDeliveryAddressList={setDeliveryAddressList}
                              setSelectedInvoiceAddress={setSelectedInvoiceAddress}
                              setSelectedDeliveryAddress={setSelectedDeliveryAddress}
                              setLoading={setLoading}
                              validateGSTNumber={validateGSTNumber}
                              mapGSTToFormValuesDynamic={mapGSTToFormValuesDynamic}
                              gstInfo={gstInfo}
                              setGstInfo={setGstInfo}
                              setSwitchToggle={setSwitchToggle}
                              copyInvoiceToDelivery={copyInvoiceToDelivery}
                              isPanCustomer={isPanCustomer}
                              isB2BEnabled={isB2BEnabled}
                            />
                          )}
                        </div>
                      )}

                      {/* ── Operational toggles (PAN / NRI / Credit) ── */}
                      {activeTabName.toLowerCase() === "contact" && (
                        <CustomerOperationalToggles
                          retailCustomerOperationalSettings={retailCustomerOperationalSettings}
                          isPanCustomer={isPanCustomer}
                          setIsPanCustomer={setIsPanCustomer}
                          panCustomerInfo={panCustomerInfo}
                          setPanCustomerInfo={setPanCustomerInfo}
                          isNriBilling={isNriBilling}
                          setIsNriBilling={setIsNriBilling}
                          isCreditSale={isCreditSale}
                          setIsCreditSale={setIsCreditSale}
                          setIsPanUpdateMode={setIsPanUpdateMode}
                          clearPanFields={clearPanFields}
                        />
                      )}

                      {/* ── Dynamic field groups ───────────────────── */}
                      <div
                        style={{
                          overflowX: "hidden",
                          marginTop: !retailCustomerOperationalSettings?.enablePanCustomer || !retailCustomerOperationalSettings?.enableNriCustomer ? "2vh" : "0vh",
                        }}
                      >
                        {groupedFields.map((group) => {
                          const isExpanded = group.collapsible ? !switchToggle : true;
                          return (
                            <div key={group.groupId}>
                              {/* Group header */}
                              {group.groupId !== "" && (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginTop: "2vh",
                                    marginBottom: group.groupName ? "2vh" : "1vh",
                                  }}
                                >
                                  <div style={{ fontWeight: 600, fontSize: "18px", color: "#0F0718" }}>{group.groupName}</div>
                                  {group.collapsible && (
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                      <Switch
                                        checked={switchToggle}
                                        onChange={(checked) => {
                                          setSwitchToggle(checked);
                                          const currentGroupId = group.groupId;
                                          const currentGroupFields = group.fields || [];

                                          if (!checked) {
                                            const emptyGroupValues = {};
                                            currentGroupFields.forEach((field) => {
                                              const key = toCamelCase(field.name);
                                              emptyGroupValues[key] = "";
                                            });
                                            const finalPayload = { [currentGroupId]: emptyGroupValues };
                                            customerForm.setFieldsValue(finalPayload);
                                            setFormDataCache((prev) => ({
                                              ...prev,
                                              [activeTab]: {
                                                ...(prev[activeTab] || {}),
                                                [currentGroupId]: {
                                                  ...((prev[activeTab] || {})[currentGroupId] || {}),
                                                  ...emptyGroupValues,
                                                },
                                              },
                                            }));
                                            return;
                                          }

                                          const allFormValues = customerForm.getFieldsValue();
                                          const copiedValues = {};
                                          const otherGroupsInSameTab = groupedFields.filter((g) => g.groupId !== currentGroupId);
                                          currentGroupFields.forEach((field) => {
                                            const fieldName = field.name.trim();
                                            for (const otherGroup of otherGroupsInSameTab) {
                                              const otherGroupValues = allFormValues[otherGroup.groupId] || {};
                                              if (Object.prototype.hasOwnProperty.call(otherGroupValues, fieldName) && otherGroupValues[fieldName] !== "") {
                                                copiedValues[currentGroupId] = copiedValues[currentGroupId] || {};
                                                copiedValues[currentGroupId][field.name] = otherGroupValues[fieldName];
                                                break;
                                              }
                                            }
                                          });
                                          setTimeout(() => {
                                            customerForm.setFieldsValue(copiedValues);
                                          }, 0);
                                          setFormDataCache((prev) => ({
                                            ...prev,
                                            [activeTab]: { ...(prev[activeTab] || {}), ...copiedValues },
                                          }));
                                        }}
                                      />
                                      <span style={{ fontWeight: 500, fontSize: "14px", color: "#0F0718" }}>{group.description || "Toggle"}</span>
                                    </div>
                                  )}
                                </div>
                              )}

                              {isExpanded && (
                                <>
                                  {/* Address selector dropdown */}
                                  {group.groupName?.toLowerCase().includes("address") && (
                                    <div style={{ marginBottom: "16px" }}>
                                      {(() => {
                                        const name = group.groupName?.toLowerCase() || "";
                                        const isInvoice = name.includes("invoice") || name.includes("invoic");
                                        const isAddressGroup = name.includes("address");
                                        const addressList = isInvoice ? selectedB2BCustomer?.invoiceAddresses || [] : selectedB2BCustomer?.deliveryAddresses || [];
                                        if (!isAddressGroup || (isInvoice && addressList.length <= 1) || addressList.length === 0) return null;
                                        return (
                                          <>
                                            <label style={{ fontWeight: 600, fontSize: "1.05em", color: "#0F0718", display: "block", marginBottom: "8px" }}>
                                              Select {group.groupName}
                                            </label>
                                            <Select
                                              placeholder={`Select ${group.groupName}`}
                                              className="custom-select"
                                              style={{ width: "70%", height: "44px", lineHeight: "44px", borderRadius: "6px", border: "1px solid #92909880" }}
                                              value={isInvoice ? selectedInvoiceAddress : selectedDeliveryAddress}
                                              onChange={(value) => {
                                                if (isInvoice) {
                                                  const selectedAddr = selectedB2BCustomer?.invoiceAddresses?.find((a) => a.s_customer_address_id === value);
                                                  setSelectedInvoiceAddress(value);
                                                  fillAddressFields(selectedAddr, group, "invoice", selectedB2BCustomer);
                                                } else {
                                                  const selectedAddr = selectedB2BCustomer?.deliveryAddresses?.find((a) => a.s_customer_address_id === value);
                                                  setSelectedDeliveryAddress(value);
                                                  fillAddressFields(selectedAddr, group, "delivery", selectedB2BCustomer);
                                                }
                                              }}
                                            >
                                              {addressList.map((addr) => (
                                                <Option key={addr.s_customer_address_id} value={addr.s_customer_address_id}>
                                                  {`${addr.line1 || ""}, ${addr.line2 || ""}, ${addr.city || ""} - ${addr.pincode || ""}`}
                                                </Option>
                                              ))}
                                            </Select>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  )}

                                  {/* Individual fields */}
                                  <Row gutter={[16, 0]}>
                                    {group.fields
                                      .filter((field) => {
                                        const isCurrentTab = field.tabId === activeTab;
                                        const isVisible = field.visible;
                                        const isBusinessTab = activeTabName.toLocaleLowerCase() === "business";
                                        const isContactTab = activeTabName.toLocaleLowerCase() === "contact";
                                        const allowBusinessFields = !isBusinessTab || (isBusinessTab && isB2BEnabled);
                                        if (isContactTab && field.name.toLowerCase().includes("pan") && !isPanCustomer) return false;
                                        const fn = field.name?.toLowerCase().replace(/\s+/g, "");
                                        const isNriField = fn?.startsWith("passport") || fn?.startsWith("visa") || fn?.startsWith("immigration");
                                        const isCreditLimitField = fn?.startsWith("credit");
                                        if (isContactTab && isNriField && !isNriBilling) return false;
                                        if (isContactTab && isCreditLimitField && !isCreditSale) return false;
                                        return isCurrentTab && isVisible && allowBusinessFields;
                                      })
                                      .sort((a, b) => {
                                        const isContactTab = activeTabName.toLowerCase() === "contact";
                                        const isPanField = (f) => f.name?.toLowerCase().includes("pan");
                                        const isNriField = (f) => {
                                          const fn = f?.name?.toLowerCase();
                                          return fn?.startsWith("passport") || fn?.startsWith("visa") || fn?.startsWith("immigration");
                                        };
                                        const getNriPriority = (field) => {
                                          const name = field?.name?.toLowerCase() || "";
                                          if (name.startsWith("passport")) return 1; // highest priority
                                          if (name.startsWith("immigration") || name.startsWith("visa")) return 2;
                                          return 3;
                                        };
                                        const isCreditField = (f) => f?.name?.toLowerCase().replace(/\s+/g, "").startsWith("credit");
                                        if (isContactTab && isPanCustomer) {
                                          const aIsPan = isPanField(a);
                                          const bIsPan = isPanField(b);
                                          if (aIsPan && !bIsPan) return -1;
                                          if (!aIsPan && bIsPan) return 1;
                                        }
                                        if (isContactTab && isNriBilling) {
                                          return getNriPriority(a) - getNriPriority(b);
                                        }
                                        if (isContactTab && isCreditSale) {
                                          const aIsCredit = isCreditField(a);
                                          const bIsCredit = isCreditField(b);
                                          if (aIsCredit && !bIsCredit) return -1;
                                          if (!aIsCredit && bIsCredit) return 1;
                                        }
                                        return a.order - b.order;
                                      })
                                      .map((field) => (
                                        <CustomerFormField
                                          key={field.id}
                                          field={field}
                                          group={group}
                                          activeTabName={activeTabName}
                                          isPanCustomer={isPanCustomer}
                                          isNriBilling={isNriBilling}
                                          isCreditSale={isCreditSale}
                                          isPanUpdateMode={isPanUpdateMode}
                                          panCustomerInfo={panCustomerInfo}
                                          formDataCache={formDataCache}
                                          activeTab={activeTab}
                                          customerForm={customerForm}
                                          optionsMap={optionsMap}
                                          handleChange={handleChange}
                                          handleApiEnterValidation={handleApiEnterValidation}
                                          handleFileUpload={handleFileUpload}
                                          fetchOptions={fetchOptions}
                                          documents={documents}
                                          setDocuments={setDocuments}
                                          selectedEditOldCustomer={selectedEditOldCustomer}
                                        />
                                      ))}
                                  </Row>
                                  {group?.groupName && <hr style={{ border: "none", borderTop: "1px solid #d1d5db", marginBottom: "16px" }} />}
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <CustomerCouponTab
                      cart={cart}
                      activeTabName={activeTabName}
                      processCouponApplication={processCouponApplication}
                      setDisplayAddNewCustomer={setDisplayAddNewCustomer}
                      setDisplayEditOldCustomer={setDisplayEditOldCustomer}
                      setAdditionalInfo={setAdditionalInfo}
                      setShowCouponOtpPopup={setShowCouponOtpPopup}
                      setCouponVerificationData={setCouponVerificationData}
                      loading={loading}
                      setLoading={setLoading}
                    />
                  </Scrollbars>
                </>
              ) : (
                /* ══════════════════════════════════════════════════════
                   LEGACY FORM (no retailCustomerFields configured)
                ══════════════════════════════════════════════════════ */
                <LegacyContactForm
                  form={form}
                  cart={cart}
                  selectedEditOldCustomer={selectedEditOldCustomer}
                  posConfig={posConfig}
                  loyalityType={loyalityType}
                  enableLoyality={enableLoyality}
                  onSwitchChange={onSwitchChange}
                  toggleCustomer={toggleCustomer}
                  setToggleCustomer={setToggleCustomer}
                  open={open}
                  showVirtualKeyboard={showVirtualKeyboard}
                  refs={refs}
                  getInputValue={getInputValue}
                  onChangeInput={onChangeInput}
                  onFocusInput={onFocusInput}
                  keyboardInputFields={keyboardInputFields}
                  setKeyboardInputFields={setKeyboardInputFields}
                  keyboard={keyboard}
                  renderThumb={renderThumb}
                  renderThumbHorizontalScroll={renderThumbHorizontalScroll}
                />
              )}

              {/* ── Footer: Cancel + Register/Update ─────────────── */}
              <CustomerDrawerFooter
                customerForm={customerForm}
                isSubmittingCustomer={isSubmittingCustomer}
                setIsSubmittingCustomer={setIsSubmittingCustomer}
                isPanMandatoryNow={isPanMandatoryNow}
                enteredPAN={enteredPAN}
                isPanUpdateMode={isPanUpdateMode}
                setIsPanUpdateMode={setIsPanUpdateMode}
                isAddNewCustomer={props.isAddNewCustomer}
                isNriBilling={isNriBilling}
                isCreditSale={isCreditSale}
                retailCustomerFields={retailCustomerFields}
                retailCustomerTabs={retailCustomerTabs}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                setActiveTabName={setActiveTabName}
                isB2BEnabled={isB2BEnabled}
                formDataCache={formDataCache}
                setFormDataCache={setFormDataCache}
                groupedFields={groupedFields}
                addNewCustomer={addNewCustomer}
                editOldCustomer={editOldCustomer}
                addCustomer={addCustomer}
                retailCustomerGroups={retailCustomerGroups}
                setDisplayAddNewCustomer={setDisplayAddNewCustomer}
                setIsB2BEnabled={setIsB2BEnabled}
                setPanCustomerInfo={setPanCustomerInfo}
                setToggleCustomer={setToggleCustomer}
                cart={cart}
                tillData={tillData}
                posConfig={posConfig}
                setOpenSalesRep={setOpenSalesRep}
                isPanCustomer={isPanCustomer}
                panCustomerInfo={panCustomerInfo}
                panThreshold={panRule?.value}
                documents={documents}
                setDocuments={setDocuments}
                handleFileUpload={handleFileUpload}
                isViewMode={isViewMode}
              />
            </Form>
          </div>
        </Spin>
      </Drawer>
    </Fragment>
  );
};

export default AddCustomerDrawer;
